import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  AdjustmentsIcon,
  CurrencyDollarIcon,
  OfficeBuildingIcon,
  CashIcon,
} from '@heroicons/react/outline';
import { DropdownMenu, MenuItem } from '@mosey/components/menus/DropdownMenu';
import { useDocumentCenterData } from '../utils/hooks';

export const CategoryFilter = () => {
  const { categories, types, documents } = useDocumentCenterData();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const currentCategoryCode = searchParams.get('category')?.toLowerCase();
  const currentCategory = categories.find(
    (category) => category.id.toString() === currentCategoryCode,
  );

  const typeMap = Object.fromEntries(types.map((t) => [t.id, t]));

  const categoryIdsWithDocs = new Set(
    documents
      .map((doc) => typeMap[doc.document_type_id]?.document_category_id)
      .filter((id): id is number => typeof id === 'number'),
  );

  const handleCategoryChange = (categoryId: string | null) => {
    const updatedParams = new URLSearchParams(searchParams);

    if (categoryId) {
      updatedParams.set('category', categoryId);

      const docsInCategory = documents.filter((doc) => {
        const type = typeMap[doc.document_type_id];
        return type?.document_category_id?.toString() === categoryId;
      });

      if (docsInCategory.length > 0) {
        const firstDoc = docsInCategory[0];
        navigate({
          pathname: `/documents/${firstDoc.id}`,
          search: updatedParams.toString(),
        });
        return;
      }
    } else {
      updatedParams.delete('category');
    }

    navigate({
      pathname: window.location.pathname,
      search: updatedParams.toString(),
    });
  };

  return (
    <DropdownMenu
      buttonText={currentCategory?.name ?? 'All Categories'}
      ariaButtonText="Change category filter:"
      ButtonLeftIcon={AdjustmentsIcon}
    >
      <MenuItem
        key="All Categories"
        as="button"
        aria-label="Filter documents to All Categories"
        LeftIcon={AdjustmentsIcon}
        aria-pressed={!currentCategoryCode}
        selected={!currentCategoryCode}
        onClick={() => handleCategoryChange(null)}
      >
        All Categories
      </MenuItem>

      {categories
        .filter((category) => categoryIdsWithDocs.has(category.id))
        .map((category) => {
          let iconName;
          switch (category.name) {
            case 'Payroll':
              iconName = CurrencyDollarIcon;
              break;
            case 'Entity':
              iconName = OfficeBuildingIcon;
              break;
            case 'Tax':
              iconName = CashIcon;
              break;
            default:
              iconName = AdjustmentsIcon;
          }

          return (
            <MenuItem
              key={category.name}
              as="button"
              aria-label={`Filter categories to ${category.name}`}
              LeftIcon={iconName}
              aria-pressed={category.id.toString() === currentCategoryCode}
              selected={category.id.toString() === currentCategoryCode}
              onClick={() => handleCategoryChange(category.id.toString())}
            >
              {category.name}
            </MenuItem>
          );
        })}
    </DropdownMenu>
  );
};
